import type { HeadlineKey } from "../site-config";
import { RotatingWord } from "./RotatingWord";
import { FadeWord } from "./FadeWord";

// The headline's last word cycles. All five are short enough to sit on the same
// line as "embedded in your" at the largest headline size — longer words push
// the phrase onto a third line. "team" leads: plain English before any motion.
const ROTATING_WORDS = ["team", "stack", "work", "life", "day"];

interface HeroProps {
  operator: string;
  location: string;
  headlineKey: HeadlineKey;
}

interface HeroVariant {
  eyebrow: string;
  /** Two lines — the second renders in a dimmer ink. */
  headline: [string, string];
  body: string;
}

const HERO_VARIANTS: Record<HeadlineKey, HeroVariant> = {
  forward: {
    eyebrow: "Forward-deployed engineering",
    headline: ["Software engineering,", "embedded in your team."],
    body: "12 years building production systems — now applied to agentic AI. I work alongside founders and product teams to ship the part you can't outsource: real workflows, real users, real reliability.",
  },
  applied: {
    eyebrow: "Applied AI, shipped",
    headline: ["Get the AI feature", "out the door."],
    body: "A senior engineer who actually ships agentic systems in production. From eval harness to retrieval to deploy — I move with your team, not adjacent to it.",
  },
  systems: {
    eyebrow: "Systems engineering for one",
    headline: ["From the chip", "to the cloud."],
    body: "Full-stack product engineering, agentic AI systems, and self-hosted infrastructure — built by one engineer, end to end. 12 years of receipts.",
  },
};

export function Hero({ operator, location, headlineKey }: HeroProps) {
  const variant = HERO_VARIANTS[headlineKey];

  const meta = [
    { label: "Operator", value: operator },
    { label: "Discipline", value: "Applied AI · Full-stack · Systems" },
    { label: "Experience", value: "12 years" },
    { label: "Based", value: location },
  ];

  return (
    <section
      id="top"
      style={{
        position: "relative",
        overflow: "hidden",
        padding: "calc(var(--section-y, 120px) * 0.6) 32px var(--section-y, 120px)",
      }}
    >
      {/* Topology lives in the Backdrop now — the hero sits directly on it. */}
      <div
        className="container"
        style={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr)",
          gap: 32,
        }}
      >
        <div className="fade-in" style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "6px 12px",
              border: "1px solid var(--hairline-strong)",
              borderRadius: 999,
              background: "color-mix(in oklab, var(--beam) 6%, transparent)",
            }}
          >
            <span className="live-dot" />
            <span
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 500,
                fontSize: 11,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "var(--ink-2)",
              }}
            >
              Fully committed · reachouts welcome
            </span>
          </div>
          <span className="tape">{location}</span>
          <span className="tape">Solo practice · est. 2026</span>
        </div>

        <div className="rule-eyebrow fade-in-1" style={{ marginTop: 24 }}>
          {variant.eyebrow}
        </div>

        <h1
          className="hero-headline fade-in-2"
          style={{
            margin: 0,
            fontFamily: "var(--font-display)",
            fontWeight: 300,
            fontSize: "clamp(48px, 8.4vw, 108px)",
            lineHeight: 0.96,
            letterSpacing: "-0.025em",
            color: "var(--ink)",
            maxWidth: 1100,
          }}
        >
          {headlineKey === "forward" ? (
            <>
              <FadeWord words={["Software", "Agentic"]} /> engineering,
            </>
          ) : (
            variant.headline[0]
          )}
          <br />
          <span style={{ color: "var(--ink-3)" }}>
            {headlineKey === "forward" ? (
              <>
                embedded in your <RotatingWord words={ROTATING_WORDS} />.
              </>
            ) : (
              variant.headline[1]
            )}
          </span>
        </h1>

        <p className="lead fade-in-3" style={{ margin: 0, maxWidth: 640, fontSize: 19, color: "var(--ink-2)" }}>
          {variant.body}
        </p>

        <div className="fade-in-4" style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8 }}>
          <a href="#contact" className="btn btn-primary">
            Send an email
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14" />
              <path d="m12 5 7 7-7 7" />
            </svg>
          </a>
          <a href="#work" className="btn btn-ghost">
            See selected work
          </a>
        </div>

        <div
          className="fade-in-5 meta-strip"
          style={{
            marginTop: 40,
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: 0,
            borderTop: "1px solid var(--hairline)",
            paddingTop: 24,
          }}
        >
          {meta.map((item) => (
            <div key={item.label} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: "var(--ink-4)",
                }}
              >
                {item.label}
              </span>
              <span
                style={{
                  fontFamily: "var(--font-display)",
                  fontWeight: 500,
                  fontSize: 14,
                  color: "var(--ink)",
                }}
              >
                {item.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
